import { useState } from 'react';
import { FaArrowDown, FaExpandArrowsAlt, FaCheckCircle } from 'react-icons/fa';
import { CtaButtons } from '../components/landing';
import { PillButtons, Button } from '../components/input';
import { CodeBlock } from '../components/code';
import { RunCrawl, RunExtract, RunScrape } from '../components/run';
import { narrow } from '../shared';
import { discordUrl, colors } from '../constants';

const headerStyle = {
  fontWeight: 'bold',
  fontSize: 38,
  textAlign: 'center',
  marginBottom: 20,
};

const subHeaderStyle = {
  textAlign: 'center',
  fontSize: 22,
  fontWeight: 'bold',
  marginBottom: 20,
};

const HowToUse = () => {
  const [active, setActive] = useState('mcp');
  const [hover, setHover] = useState();
  const [pressed, setPressed] = useState();

  const choices = [
    ['mcp', 'MCP Server', '/icons/mcp.png'],
    ['python', 'Python', '/icons/python-black.svg'],
    ['javascript', 'JavaScript', '/icons/javascript-black.svg'],
    ['http', 'HTTP', '/icons/http.svg'],
  ];

  const padding = 30;

  const activeStyle = {
    backgroundx: '#eee',
    border: `2px solid ${colors.primary}`,
    colorx: 'white',
    padding: padding - 1,
  };

  const hoverStyle = {
    border: '1px solid #888',
    backgroundx: '#f3f3f3',
  };

  const pressedStyle = {
    boxShadow: 'none',
    position: 'relative',
    top: 2,
    left: 2,
  };

  const nodes = choices.map(([choice, name, src]) => (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        width: 160,
        height: null,
        border: '1px solid #ddd',
        boxShadow: '2px 2px #eee',
        borderRadius: 20,
        padding,
        cursor: 'pointer',
        ...(hover == choice ? hoverStyle : {}),
        ...(active == choice ? activeStyle : {}),
        ...(pressed == choice ? pressedStyle : {}),
      }}
      onMouseOver={() => setHover(choice)}
      onMouseLeave={() => setHover()}
      onMouseDown={() => setPressed(choice)}
      onMouseUp={() => setPressed()}
      onClick={() => setActive(choice)}
    >
      <div style={{ marginBottom: 8 }}>
        <img src={src} style={{ width: 40 }} />
      </div>
      <div style={{ fontSize: 18 }}>{name}</div>
    </div>
  ));

  return (
    <div style={{ maxWidth: 800, margin: '0 auto', marginTop: 40 }}>
      <div style={{ ...headerStyle, marginBottom: 60 }}>How do you want to use FetchFox?</div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>{nodes}</div>
    </div>
  );
};

const McpChoices = () => {
  const [tab, setTab] = useState('python');

  const choices = [['python', 'Python (pydantic)']];

  const code = {
    python: `# pip install pydantic-ai

from pydantic_ai import Agent
from pydantic_ai.mcp import MCPServerStreamableHTTP
from pydantic import BaseModel

fetchfox = MCPServerStreamableHTTP(
    url='https://mcp.fetchfox.ai/mcp',
    headers={"Authorization": "Bearer YOUR_API_KEY"})

agent = Agent('openai:gpt-4o', toolsets=[fetchfox])
result = agent.run_sync(
  'Scrape https://pokemondb.net/pokedex/* for 10 pokemon names and numbers'
)`,
    javascript: `// npm install modelcontextprotocol

import { Client } from "modelcontextprotocol/client";
import { HTTPServerTransport } from "modelcontextprotocol/transport/http";

async function run() {
  const client = new Client(
    { name: "demo", version: "1.0.0" },
    new HTTPServerTransport({ url: "http://localhost:3000/mcp" })
  );

  await client.connect();
  console.log(await client.listTools());

  const result = await client.callTool("echo", { message: "hi" });
  console.log(result);

  await client.close();
}

run();
`,
  };

  return (
    <>
      <PillButtons choices={choices} value={tab} onChange={setTab} />
      <CodeBlock language={tab}>{code[tab]}</CodeBlock>
      <FaArrowDown size="36" color="#888" />
    </>
  );
};

const ApiCall = () => {
  const [tab, setTab] = useState('python');

  const codeStyle = {
    // display: 'inline-block',
    // width: 560,
    // whiteSpace: 'pre',
    // fontFamily: 'monospace',
    // background: '#f3f3f3',
    // borderRadius: 4,
  };

  const choices = [
    ['python', 'Python'],
    ['javascript', 'JavaScript'],
    ['mcp', 'MCP Server'],
    ['pydantic', 'Pydantic'],
    ['bash', 'curl'],
  ];

  const code = {
    python: `# pip install fetchfox-sdk

from fetchfox_sdk import FetchFox

fox = FetchFox(api_key="YOUR_API_KEY")

data = fox.scrape({
  "pattern": 'https://www.reddit.com/r/*/comments/*',
  "template": "username, comment, points",
})`,

    javascript: `// npm install fetchfox-sdk

import * as fox from 'fetchfox-sdk';

fox.configure({ apiKey: 'YOUR_API_KEY' })

const data = await fox.scrape({
  pattern: 'https://www.reddit.com/r/*/comments/*',
  template: 'username, comment, points'
})`,

    mcp: `{
  "url": "https://mcp.fetchfox.ai/mcp",
  "transport": {
    "type": "http",
    "headers": {
      "Authorization": "Bearer YOUR_API_KEY"
    }
}

# And then ask your agent to scrape something`,

    pydantic: `from pydantic_ai import Agent
from pydantic_ai.mcp import MCPServerStreamableHTTP

fetchfox = MCPServerStreamableHTTP(
    url='https://mcp.fetchfox.ai/mcp',
    headers={"Authorization": "Bearer YOUR_API_KEY"})

agent = Agent('openai:gpt-4o', toolsets=[fetchfox])
result = agent.run_sync(
  'Scrape username, text, and points from '
  'https://www.reddit.com/r/*/comments/*')`,

    bash: `curl -X POST https://api.fetchfox.ai/api/scrape \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer $FETCHFOX_API_KEY" \\
  -d '{"pattern":"https://www.reddit.com/r/*/comments/*",
       "template": "username, comment, points"}'`,
  };

  const language =
    {
      mcp: 'bash',
      pydantic: 'python',
    }[tab] || tab;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
      <br />

      <div style={subHeaderStyle}>How do you want to use FetchFox?</div>
      <PillButtons choices={choices} value={tab} onChange={setTab} />

      <CodeBlock language={language}>{code[tab]}</CodeBlock>
      <FaArrowDown size="36" color="#888" />

      {/*tab == 'mcp' && <McpChoices />*/}

      <CodeBlock language="json">
        {`{
    "username": "BobbyUser",
    "comment_text": "AI + FetchFox = 🤩",
    "points": 888
}`}
      </CodeBlock>

      <div style={{ color: '#888', fontSize: 14, margin: '40px 0 0 0' }}>🪦 BeautifulSoup 2004-2025, RIP</div>
    </div>
  );
};

export function LandingNew() {
  return (
    <div style={{ marginBottom: 200 }}>
      <div style={{ textAlign: 'center', marginBottom: 20 }}>
        <img src="/logo-fox.svg" style={{ width: 60, marginTop: 20 }} />
        <div
          style={{
            color: 'black',
            fontWeight: 'bold',
            fontSize: 48,
            textAlign: 'center',
            marginTop: 10,
          }}
        >
          The AI Scraping API
        </div>
        <div
          style={{
            color: '#444',
            fontWeightx: 'bold',
            fontSize: 24,
            textAlign: 'center',
            marginBottom: 10,
            marginBottom: 8,
          }}
        >
          Scrape millions of items with one line of code. Zero blocks.
        </div>
      </div>

      <div style={{ width: 400, margin: '0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <CodeBlock language="pthon">{`scrape("https://www.reddit.com/r/*/comments/*")
`}</CodeBlock>
        <FaArrowDown size="36" color="#888" />
        <CodeBlock language="json">
          {`[
    {
        "username": "BobbyUser",
        "comment_text": "AI + FetchFox = 🤩",
        "points": 187
    },
    {
        "username": "Mr Developer",
        "comment_text": "🪦 BeautifulSoup 2004-2025",
        "points": 74
    },
    // ...
]`}
        </CodeBlock>
      </div>

      <div
        style={{
          color: '#444',
          fontWeightx: 'bold',
          fontSize: 18,
          textAlign: 'center',
          marginTop: 20,
          marginBottom: 20,
        }}
      >
        Built For Developers and AI agents
      </div>

      <CtaButtons />

      <br />
      <br />
      <br />

      {narrow(<TryIt />)}

      <br />
      <br />
      <br />

      <HowToUse />
    </div>
  );
}

const TryIt = () => {
  const [tab, setTab] = useState('crawl');

  const padding = 10;
  const tabStyle = {
    border: '1px solid #fff',
    flex: 1,
    padding,
    cursor: 'pointer',
  };

  const nameStyle = {
    fontWeight: 'bold',
    fontSize: 12,
    fontFamily: 'monospace',
    textAlign: 'center',
  };

  const textStyle = {
    fontSize: 12,
    display: 'none',
  };

  const activeStyle = {
    border: `1px solid ${colors.primary}`,
    background: colors.primary,
    color: 'white',
    padding: padding,
    borderRadius: 10,
  };

  const blurbStyle = {
    fontSize: 14,
    // borderBottom: '1px solid #ddd',
    // paddingBottom: 20,
    marginBottom: 10,
  };

  const runStyle = { border: '1px solid #ccc', padding: 10, borderRadius: 10 };

  const active = (style, t) => (t == tab ? { ...style, ...activeStyle } : style);

  const node = {
    crawl: (
      <>
        <div style={blurbStyle}>
          The <b>crawl</b> endpoint takes a <b>URL pattern</b> and finds a list of matching <b>URLs</b>.
        </div>
        <div style={runStyle}>
          <RunCrawl hideAdvanced />
        </div>
      </>
    ),

    extract: (
      <>
        <div style={blurbStyle}>
          The <b>extract</b> endpoint converts <b>URLs</b> and an <b>item template</b> into <b>structured data</b>.
        </div>
        <div style={runStyle}>
          <RunExtract hideAdvanced />
        </div>
      </>
    ),

    scrape: (
      <>
        <div style={blurbStyle}>
          The <b>scrape</b> endpoint uses a <b>URL pattern</b> and an <b>item template</b> to generate structured data.
          It is a combination of crawl and extract.
        </div>
        <div style={runStyle}>
          <RunScrape hideAdvanced />
        </div>
      </>
    ),
  }[tab];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={headerStyle}>Try the API</div>

      <div style={{ display: 'flex', gap: 10 }}>
        <div style={active(tabStyle, 'crawl')} onClick={() => setTab('crawl')}>
          <div style={nameStyle}>crawl(pattern)</div>
          <div style={textStyle}>Find URLs using a pattern</div>
        </div>

        <div style={active(tabStyle, 'extract')} onClick={() => setTab('extract')}>
          <div style={nameStyle}>extract(urls, template)</div>
          <div style={textStyle}>Turn URLs into JSON</div>
        </div>
        <div style={active(tabStyle, 'scrape')} onClick={() => setTab('scrape')}>
          <div style={nameStyle}>scrape(pattern, template)</div>
          <div style={textStyle}>Find pages and get JSON</div>
        </div>
      </div>

      <div stylex={{ border: '1px solid #ccc', padding: 10, borderRadius: 10 }}>{node}</div>
    </div>
  );
};
//   Scrape <mark>any site</mark>
// </div>
// <div style={headerStyle}>
//   with <mark>just a prompt</mark>

export function LandingCurrent() {
  return (
    <div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          textAlign: 'center',
          margin: '40px auto 80px auto',
        }}
      >
        <div>
          <img src="/logo-fox.svg" style={{ width: 60, marginBottom: 10 }} />
        </div>
        <div style={headerStyle}>
          Scrape <mark>any site</mark> with just <mark>a prompt</mark>
        </div>
        <div style={{ color: '#444', textAlign: 'center', fontSize: 22, marginBottom: 10 }}>
          An AI scraper for <b>developers</b> and <b>AI agents</b>
        </div>
        <div style={{ fontSize: 15, textAlign: 'center', color: '#777' }}>
          Scrape <b style={{ color: '#555' }}>millions of pages</b> and convert them into <br /> structured data with
          just <b style={{ color: '#555' }}>one line of code</b>.
        </div>
        <CtaButtons />
      </div>

      {narrow(<ApiCall />)}

      <br />
      <br />

      <CtaButtons />
      <br />
      <br />
      <br />

      {narrow(<TryIt />)}

      <br />
      <br />
      <br />

      <div style={{ margin: '40px auto 200px auto', width: 640, textAlign: 'center', fontSize: 18 }}>
        FetchFox is a new
        <div style={{ margin: '18px 0 10px 0' }}>
          <FaArrowDown size="24" color="#aaa" />
        </div>
        Get free 1:1 support on{' '}
        <a href={discordUrl} className="bold">
          Discord
        </a>{' '}
        or at{' '}
        <a href="mailto:help@fetchfoxai.com" className="bold">
          help@fetchfoxai.com
        </a>
      </div>
    </div>
  );
}

export default LandingCurrent;
